import { useState, useEffect, useCallback } from "react";
import {
  collection, addDoc, deleteDoc, updateDoc,
  doc, query, orderBy, onSnapshot, serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { subMonths, startOfMonth } from "date-fns";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const FREE_LIMITS = { entriesPerMonth: 30, accounts: 2, historyMonths: 3 };

export function useEntries() {
  const { user, profile, isPro } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Workspace activo: el personal por defecto, o el compartido si el
  // usuario ha cambiado a uno. Mientras el perfil no tenga workspaces
  // asignados (cuentas antiguas sin migrar), se usa users/{uid} como antes.
  const workspaceId = profile?.activeWorkspaceId || profile?.personalWorkspaceId || null;
  const entriesRef = useCallback(() => {
    if (workspaceId) return collection(db, "workspaces", workspaceId, "entries");
    return collection(db, "users", user.uid, "entries");
  }, [workspaceId, user]);

  useEffect(() => {
    if (!user) return;
    const ref = entriesRef();
    const q   = query(ref, orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      // Ordenar por datetime real (DD/MM/YYYY HH:MM) en vez de createdAt,
      // para que entradas con fecha pasada aparezcan en su posición correcta
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => {
        const parseDate = (dt) => {
          if (!dt) return 0;
          // formato DD/MM/YYYY HH:MM
          const [datePart, timePart] = dt.split(" ");
          const [d2, m, y] = (datePart || "").split("/");
          const [h, min] = (timePart || "00:00").split(":");
          return new Date(y, m-1, d2, h, min).getTime();
        };
        return parseDate(b.datetime) - parseDate(a.datetime);
      });
      setEntries(docs);
      setLoading(false);
    });
    return unsub;
  }, [user, workspaceId]);

  const visibleEntries = useCallback(() => {
    if (isPro) return entries;
    const cutoff = subMonths(startOfMonth(new Date()), FREE_LIMITS.historyMonths);
    return entries.filter(e => {
      const d = new Date(e.year, MONTHS.indexOf(e.month), 1);
      return d >= cutoff;
    });
  }, [entries, isPro]);

  const thisMonthCount = useCallback(() => {
    const now = new Date();
    return entries.filter(e =>
      e.year === now.getFullYear() && e.month === MONTHS[now.getMonth()]
    ).length;
  }, [entries]);

  async function addEntry(data) {
    if (!user) return { error: "No autenticado" };
    if (!isPro && thisMonthCount() >= FREE_LIMITS.entriesPerMonth)
      return { error: `Plan gratuito: máximo ${FREE_LIMITS.entriesPerMonth} entradas por mes. Hazte Pro por 1,99€/mes.` };
    const created = await addDoc(entriesRef(), {
      ...data, addedBy: user.uid, createdAt: serverTimestamp()
    });
    return { ok: true, id: created.id };
  }

  async function deleteEntry(id) {
    if (workspaceId) {
      await deleteDoc(doc(db, "workspaces", workspaceId, "entries", id));
    } else {
      await deleteDoc(doc(db, "users", user.uid, "entries", id));
    }
  }

  async function updateEntry(id, data) {
    if (workspaceId) {
      await updateDoc(doc(db, "workspaces", workspaceId, "entries", id), data);
    } else {
      await updateDoc(doc(db, "users", user.uid, "entries", id), data);
    }
  }

  return {
    entries, visibleEntries: visibleEntries(), loading,
    addEntry, deleteEntry, updateEntry,
    isPro, FREE_LIMITS, thisMonthCount: thisMonthCount()
  };
}

